import { FC, useEffect, useMemo, useState } from 'react';

import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { useYearQuarterNavigation } from 'hooks';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { getRosterDutyHourTypes } from 'RestAPIs/roster/duty-hour-types';
import { RosterFunction, Scenario } from 'components/types';

import { Units } from 'components/enums';

import { getMaxDate } from 'utils/date';

import { NonPlannedHourPayload } from 'RestAPIs/capacity-plan/baseplan';
import { getRosterFunctionsWithPreferred } from 'RestAPIs/roster/functions';

import { scenarioAtom } from '../../atoms';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, DialogActions, Grid, TextField } from '@mui/material';

import {
  ControlledDutyHourTypeSelector,
  DatePicker,
  Dialog,
  FontAwesomeSvgIcon,
  Select,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';
import NumberField from 'components/shared/NumberField';

import { SaveAndUpdateOptions } from '../WorkSupply';

interface NonPlannedHoursDialogProps {
  editValues: any;
  isOpen: boolean;
  handleClose: () => void;
  saveAndUpdate: (params: SaveAndUpdateOptions) => Promise<void>;
  loading: boolean;
}

const defaultFormValues = {
  costCenterId: undefined,
  function: undefined,
  dutyTypeId: undefined,
  description: '',
  quantity: 1,
  hoursPerUnit: 0,
  unit: '',
  validFrom: new Date(new Date().getFullYear(), 0, 1),
  validTo: getMaxDate()
};
interface RosterFunctionWithPreferred extends RosterFunction {
  preferred: boolean;
}

const NonPlannedHoursDialog: FC<NonPlannedHoursDialogProps> = ({
  editValues,
  isOpen,
  handleClose,
  saveAndUpdate,
  loading
}) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom) as Scenario;
  const [rosterFunctionOptions, setRosterFunctionOptions] = useState<
    RosterFunctionWithPreferred[]
  >([]);

  const { year } = useYearQuarterNavigation();

  useEffect(() => {
    if (!scenario) {
      return;
    }

    const costCenterIds = scenario.costCenters.map(({ id }) => id);
    const dhtDate = new Date(year, 0, 1).toISOString();

    Promise.all([
      getRosterFunctionsWithPreferred({ costCenterIds }),
      getRosterDutyHourTypes({
        startDate: dhtDate,
        endDate: dhtDate
      })
    ])
      .then(([rosterFunctions]) => {
        setRosterFunctionOptions(rosterFunctions.data);
      })
      .catch(() => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      });
  }, [scenario]);

  const defaultValues = {
    ...defaultFormValues,
    validFrom: new Date(year, 0, 1)
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues
  });

  useEffect(() => {
    if (editValues) {
      reset({
        ...editValues,
        validFrom: new Date(editValues.timeframe.from),
        validTo: new Date(editValues.timeframe.to)
      });
    } else {
      reset(defaultValues);
    }
  }, [editValues]);

  const isEditMode = !!editValues;

  const onSubmit = async (data) => {
    const {
      costCenterId,
      description,
      dutyTypeId,
      function: fn,
      quantity,
      unit,
      hoursPerUnit,
      validFrom,
      validTo
    } = data;

    const costCenter = scenario.costCenters.find(
      (cc) => cc.id === costCenterId
    );

    if (!costCenter) return;

    const payload: NonPlannedHourPayload = {
      costCenter,
      description,
      dutyTypeId,
      function: fn,
      functionId: fn.id,
      unit,
      quantity,
      hoursPerUnit,
      timeframe: { from: validFrom.toISOString(), to: validTo.toISOString() }
    };

    if (isEditMode && !editValues.rowId) {
      await saveAndUpdate({
        req: 'PUT',
        id: editValues.id,
        payload
      });
    } else {
      await saveAndUpdate({ req: 'POST', payload });
    }
    handleClose();
  };

  const unitOptions = useMemo(
    () =>
      Object.keys(Units).map((unit) => ({
        value: unit,
        label: t(`timeUnits.${unit}`)
      })),
    []
  );

  return (
    <Dialog
      open={isOpen}
      title={isEditMode ? t('edit') : t('baseplan.nonPlannedHoursDialog.title')}
      PaperProps={{
        sx: {
          minWidth: 550
        }
      }}
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnSpacing={2} rowSpacing={4}>
          <Grid item xs={6}>
            <Controller
              name="costCenterId"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  fullWidth
                  {...field}
                  onSelected={field.onChange}
                  label={t('costCenter')}
                  options={scenario.costCenters.map((costCenter) => ({
                    label: costCenter.name,
                    value: costCenter.id
                  }))}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="function"
              control={control}
              render={({ field, fieldState }) => (
                <VirtualizedAutocomplete
                  onChange={(e, v) => {
                    field.onChange(v);
                  }}
                  optionLabelProp="description"
                  getOptionLabel={(o) => {
                    if (typeof o === 'string') {
                      return o;
                    }

                    return o?.description ?? '';
                  }}
                  options={rosterFunctionOptions}
                  value={
                    isEditMode
                      ? rosterFunctionOptions.find(
                          ({ id }) => id === editValues.function.id
                        )
                      : undefined
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.preferred ? (
                        <Box component="span" color="primary.main">
                          <Box component="span" sx={{ mr: 1 }}>
                            <FontAwesomeSvgIcon
                              icon={faStar}
                              fontSize="inherit"
                            />
                          </Box>
                          {option.description}
                        </Box>
                      ) : (
                        option.description
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('function')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      required
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  autoHighlight
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('description')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledDutyHourTypeSelector
              control={control}
              name="dutyTypeId"
              label={t('dutyHourType')}
              fullWidth
              variant="standard"
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="quantity"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <NumberField
                    label={t('quantity')}
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="hoursPerUnit"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <NumberField
                    label={t('hours')}
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="unit"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  fullWidth
                  onSelected={field.onChange}
                  label={t('unit')}
                  options={unitOptions}
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="validFrom"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  showInput
                  label={t('validFrom')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="validTo"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  showInput
                  label={t('validTo')}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </Grid>
        </Grid>

        <DialogActions sx={{ marginTop: 3 }}>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <LoadingButton loading={loading} type="submit" variant="contained">
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NonPlannedHoursDialog;
